import cn from 'classnames';
import { forwardRef } from 'react';

import { filterStyleProps } from '../../../utils';
import { useStyleProps } from '../../../utils/hooks';

import type { ReactElement } from 'react';

import type { PolymorphicRef } from '../../../utils';
import type { SkeletonComponent, SkeletonProps } from './skeleton.types';

import './skeleton.scss';

/**
 * Skeleton component provides a placeholder preview of content before it loads.
 * It's useful for improving perceived performance during loading states.
 *
 * This is a polymorphic component. The root tag can be set using the `as` prop.
 * By default, it renders a `div` element.
 *
 * @example
 * ```tsx
 * // Basic rectangular skeleton
 * <Skeleton width={200} height={40} />
 *
 * // Circular avatar placeholder
 * <Skeleton variant="circular" width={40} height={40} />
 *
 * // Inline skeleton within text
 * <p>Loading <Skeleton as="span" variant="text" width={80} /></p>
 *
 * // With pulse animation
 * <Skeleton animation="pulse" width={300} height={100} />
 * ```
 */
const Skeleton = forwardRef(
  <RootComponent extends React.ElementType>(
    props: SkeletonProps<RootComponent>,
    forwardedRef: PolymorphicRef<RootComponent>,
  ): ReactElement => {
    const { animation = 'wave', as, className, style, variant = 'rectangular', ...rest } = props;

    const Root: React.ElementType = as || 'div';

    const inlineStyle = useStyleProps({ ...props });

    const classes = cn(
      'rbui-skeleton',
      `rbui-skeleton--${variant}`,
      {
        [`rbui-skeleton--animation-${animation}`]: !!animation,
      },
      className,
    );

    return (
      <Root
        aria-busy="true"
        aria-live="polite"
        className={classes}
        ref={forwardedRef}
        role="status"
        style={{ ...inlineStyle, ...style }}
        {...filterStyleProps(rest)}
      />
    );
  },
) as SkeletonComponent;

Skeleton.displayName = 'Skeleton';

export default Skeleton;
